// Ad Generation Page - Unified ad pack generation interface with phase-linked debug view
// URL-routed: /ad-generation (list) and /ad-generation/:jobId (detail)
import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAdGenerationStore } from '@/stores/adGeneration';
import { adGenerationApi, adPackShareApi, type ShareTokenResponse } from '@/services/api';
import { AdPackConfigPanel } from '@/components/AdPackConfigPanel';
import { ExecutionLogPanel, phaseLabel } from '@/components/ExecutionLogPanel';
import { LiveAdPackView } from '@/components/LiveAdPackView';
import { AgenticDeepDiveModal, estimateTotalCost } from '@/components/AgenticDeepDiveModal';
import {
  Zap,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowLeft,
  ChevronRight,
  AlertCircle,
  User,
  Layers,
  Image,
  FileText,
  Ban,
  Download,
  Cloud,
  Star,
  Radio,
  Archive,
  ArchiveRestore,
  Eye,
  EyeOff,
  AlertTriangle,
  Sparkles,
  Share2,
  Copy,
  Check,
  ExternalLink,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { AdPackJob, AdPackStatusResponse, StepLogEntry } from '@/services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function elapsed(seconds?: number): string {
  if (!seconds || seconds < 1) return '< 1s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function timeAgo(isoStr?: string): string {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

const STATUS_COLORS: Record<string, string> = {
  running: 'bg-brand-teal/10 text-brand-teal-dark border-brand-teal/20',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  queued: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={clsx(
        'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border',
        STATUS_COLORS[status] ?? 'bg-slate-50 text-slate-500 border-slate-200',
      )}
    >
      {status}
    </span>
  );
}

function ProgressBar({ percent, failed }: { percent: number; failed?: boolean; className?: string }) {
  return (
    <div className="bg-slate-100 rounded-full overflow-hidden h-2">
      <div
        className={clsx(
          'h-full rounded-full transition-all duration-500',
          failed ? 'bg-rose-400' : 'bg-brand-teal',
        )}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Views - URL-routed for browser history support
// ---------------------------------------------------------------------------

export function AdGenerationPage() {
  const { jobId } = useParams<{ jobId?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isNewMode = location.pathname.endsWith('/new');
  const briefId = searchParams.get('brief_id') || undefined;
  const clientUid = searchParams.get('client_uid') || '';

  const handleOpenJob = (id: string) => {
    navigate(`/ad-generation/${id}`);
  };

  const handleBack = () => {
    navigate('/ad-generation');
  };

  if (isNewMode) {
    if (!clientUid) {
      return (
        <div className="p-8 text-center">
          <p className="text-slate-500">Missing client_uid parameter</p>
          <button onClick={() => navigate('/ad-generation')} className="mt-4 text-teal-500 hover:underline">
            Back to Ad Generation
          </button>
        </div>
      );
    }

    return (
      <div className="p-6 max-w-2xl mx-auto">
        <AdPackConfigPanel
          clientUid={clientUid}
          briefId={briefId}
          onSubmit={(jobId) => navigate(`/ad-generation/${jobId}`)}
          onCancel={() => navigate('/ad-generation')}
        />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {!jobId && <ListView onOpenJob={handleOpenJob} />}
      {jobId && <DetailView jobId={jobId} onBack={handleBack} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List View
// ---------------------------------------------------------------------------

function ListView({ onOpenJob }: { onOpenJob: (id: string) => void }) {
  const {
    jobs,
    total,
    loading,
    error,
    isRealtime,
    subscribeToJobs,
    unsubscribeAll,
    showArchived,
    setShowArchived,
    archiveJob,
    unarchiveJob,
    archiving,
  } = useAdGenerationStore();

  useEffect(() => {
    subscribeToJobs();
    return () => unsubscribeAll();
  }, [subscribeToJobs, unsubscribeAll]);

  const activeJobs = jobs.filter((j) => j.status === 'running' || j.status === 'pending');

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold text-navy-900">Ad Generation</h1>
        <p className="text-sm text-slate-500 mt-1">
          Monitor ad pack generation jobs across the creative pipeline.
        </p>
      </div>

      {error && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Zap} label="Total Jobs" value={total} />
        <StatCard icon={Play} label="Active" value={activeJobs.length} accent />
        <StatCard icon={CheckCircle2} label="Completed" value={jobs.filter((j) => j.status === 'completed').length} />
        <StatCard icon={XCircle} label="Failed" value={jobs.filter((j) => j.status === 'failed').length} />
      </div>

      {/* Active Jobs */}
      {activeJobs.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-brand-teal/10 flex items-center justify-center text-brand-teal border border-brand-teal/20">
              <Zap size={14} />
            </div>
            <h2 className="text-sm font-bold text-navy-900 uppercase tracking-wide">Active Jobs</h2>
            <span className="text-[10px] font-mono bg-brand-teal/10 text-brand-teal px-2 py-0.5 rounded-full border border-brand-teal/20">
              {activeJobs.length}
            </span>
          </div>
          <div className="space-y-3">
            {activeJobs.map((job) => (
              <ActiveJobCard key={job.job_id} job={job} onClick={() => onOpenJob(job.job_id)} />
            ))}
          </div>
        </div>
      )}

      {/* All Jobs Table */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-navy-900 uppercase tracking-wide">
            All Jobs
            <span className="ml-2 text-xs text-slate-400 font-normal">({total} total)</span>
          </h2>
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={clsx(
              'text-xs font-medium px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1.5',
              showArchived
                ? 'text-brand-purple bg-brand-purple/10 border-brand-purple/30'
                : 'text-slate-500 hover:text-brand-teal border-slate-200 hover:border-brand-teal/30'
            )}
          >
            {showArchived ? <EyeOff size={12} /> : <Eye size={12} />}
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </button>
        </div>

        {jobs.length > 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Job</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Client</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Phase</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Ads</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Progress</th>
                  <th className="text-left px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Created</th>
                  <th className="text-right px-4 py-3 font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.map((job) => {
                  const isArchived = job.archived ?? false;
                  const isArchivingThis = archiving === job.job_id;

                  const handleArchiveClick = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (isArchived) {
                      unarchiveJob(job);
                    } else {
                      archiveJob(job);
                    }
                  };

                  return (
                    <tr
                      key={job.job_id}
                      className={clsx(
                        'hover:bg-slate-50 cursor-pointer transition-colors',
                        isArchived && 'opacity-60'
                      )}
                      onClick={() => onOpenJob(job.job_id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[11px] text-brand-purple">{job.job_id}</span>
                          {isArchived && (
                            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 border border-slate-200">
                              Archived
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-navy-900">{job.client_uid || '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{phaseLabel(job.pipeline_phase)}</td>
                      <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                      <td className="px-4 py-3">
                        <span className="text-slate-600">{job.ads_generated}</span>
                        <span className="text-slate-300 mx-1">/</span>
                        <span className="text-slate-400">{job.ads_reviewed} reviewed</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16"><ProgressBar percent={job.percent_complete} failed={job.status === 'failed'} /></div>
                          <span className="text-[10px] font-bold text-slate-500">{Math.round(job.percent_complete)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400">{timeAgo(job.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={handleArchiveClick}
                            disabled={isArchivingThis}
                            className={clsx(
                              'p-1.5 rounded-lg border transition-all',
                              isArchivingThis
                                ? 'opacity-50 cursor-not-allowed'
                                : isArchived
                                  ? 'text-emerald-600 hover:bg-emerald-50 border-emerald-200 hover:border-emerald-300'
                                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50 border-slate-200 hover:border-slate-300'
                            )}
                            title={isArchived ? 'Unarchive job' : 'Archive job'}
                          >
                            {isArchivingThis ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : isArchived ? (
                              <ArchiveRestore size={12} />
                            ) : (
                              <Archive size={12} />
                            )}
                          </button>
                          <ChevronRight size={10} className="text-slate-300" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
            <Zap size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-400">No ad generation jobs yet</p>
            <p className="text-[11px] text-slate-400 mt-1">
              Jobs appear here when triggered via the ad pack pipeline or MCP tools.
            </p>
          </div>
        )}
      </div>

      {/* Real-time indicator */}
      <div className="flex items-center justify-center gap-2 text-[10px] font-mono">
        {loading ? (
          <span className="text-slate-400">loading...</span>
        ) : isRealtime ? (
          <span className="flex items-center gap-1.5 text-emerald-600">
            <Radio size={10} className="animate-pulse" />
            live updates
          </span>
        ) : (
          <span className="text-slate-400">polling mode</span>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Detail View
// ---------------------------------------------------------------------------

function DetailView({ jobId, onBack }: { jobId: string; onBack: () => void }) {
  const navigate = useNavigate();
  const { selectedJob, loading, error, subscribeToJob, clearSelection, cancelJob, archiveJob, archiving } = useAdGenerationStore();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [showDeepDive, setShowDeepDive] = useState(false);
  const [shareToken, setShareToken] = useState<ShareTokenResponse | null>(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);
  const [autoMintAttempted, setAutoMintAttempted] = useState(false);

  // Phase selection state for the linked phase-timeline / log view
  const [selectedPhase, setSelectedPhase] = useState<string | null>(null);
  // When true, selectedPhase auto-tracks the running pipeline phase
  const followLiveRef = useRef(true);

  useEffect(() => {
    subscribeToJob(jobId);
    return () => clearSelection();
  }, [jobId, subscribeToJob, clearSelection]);

  // Derive active/phase state from selectedJob (safe to compute before hooks)
  const isActive = ['running', 'pending', 'queued'].includes(selectedJob?.status ?? '');
  const currentPipelinePhase = selectedJob?.pipeline_phase;
  const isCompleted = selectedJob?.status === 'completed';

  // Auto-advance selectedPhase to track the running phase while following live.
  // Must be before conditional returns to satisfy Rules of Hooks.
  useEffect(() => {
    if (followLiveRef.current && isActive && currentPipelinePhase) {
      setSelectedPhase(currentPipelinePhase);
    }
    if (!isActive) {
      followLiveRef.current = false;
    }
  }, [currentPipelinePhase, isActive]);

  // Auto-mint a share token once the pack is completed so the embedded viewer
  // can load immediately without an extra user click.
  useEffect(() => {
    if (!isCompleted || shareToken || shareLoading || autoMintAttempted || !selectedJob) return;
    setAutoMintAttempted(true);
    setShareLoading(true);
    adPackShareApi
      .mintForJob(jobId, selectedJob.client_uid)
      .then((token) => setShareToken(token))
      .catch((e) => setShareError(e instanceof Error ? e.message : 'Failed to create share link'))
      .finally(() => setShareLoading(false));
  }, [isCompleted, shareToken, shareLoading, autoMintAttempted, selectedJob, jobId]);

  const handleMintShare = async () => {
    if (shareToken || shareLoading) return;
    setShareLoading(true);
    setShareError(null);
    try {
      const token = await adPackShareApi.mintForJob(jobId, selectedJob?.client_uid);
      setShareToken(token);
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Failed to create share link');
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyShareUrl = () => {
    if (!shareToken) return;
    navigator.clipboard.writeText(shareToken.share_url).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const filename = await adGenerationApi.downloadJobZip(jobId);
      if (!filename) {
        setDownloadError('Download failed. Please try again.');
      }
    } catch (err) {
      console.error('Download failed:', err);
      setDownloadError('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedJob) return;
    await archiveJob(selectedJob as Parameters<typeof archiveJob>[0]);
    onBack();
  };

  if (loading && !selectedJob) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 flex flex-col items-center shadow-sm">
        <Loader2 size={24} className="animate-spin text-brand-teal mb-3" />
        <p className="text-sm text-slate-500">Loading job detail...</p>
      </div>
    );
  }

  if (error || !selectedJob) {
    return (
      <>
        <BackButton onClick={onBack} />
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-3 flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error ?? `Job ${jobId} not found`}</span>
        </div>
      </>
    );
  }

  const job = {
    ...selectedJob,
    phases_completed: selectedJob.phases_completed ?? [],
    phases_remaining: selectedJob.phases_remaining ?? [],
    percent_complete: selectedJob.percent_complete ?? 0,
    personas_count: selectedJob.personas_count ?? 0,
    ads_generated: selectedJob.ads_generated ?? 0,
    ads_reviewed: selectedJob.ads_reviewed ?? 0,
  };
  const pct = Math.round(job.percent_complete);
  const jobIsActive = ['running', 'pending', 'queued'].includes(job.status);

  const isStale =
    jobIsActive &&
    job.phases_remaining.length === 0 &&
    job.pipeline_phase === 'package' &&
    !job.phases_completed.includes('package');

  // Build a lookup of phase id → StepLogEntry for enriching the timeline
  const stepByPhase: Record<string, StepLogEntry> = {};
  for (const step of job.execution_log?.steps ?? []) {
    stepByPhase[step.step_name] = step;
  }

  function handleSelectPhase(phase: string) {
    if (selectedPhase === phase) {
      // Toggle off → show all phases
      setSelectedPhase(null);
      followLiveRef.current = false;
    } else {
      setSelectedPhase(phase);
      // Resume live-following only if the user clicks the currently running phase
      followLiveRef.current = jobIsActive && phase === job.pipeline_phase;
    }
  }

  function handleClearFilter() {
    setSelectedPhase(null);
    followLiveRef.current = false;
  }

  const hasPhaseData = job.phases_completed.length > 0 || job.phases_remaining.length > 0;

  return (
    <>
      <div className="flex items-center justify-between">
        <BackButton onClick={onBack} />
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDeepDive(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-navy-900 bg-brand-teal/10 border border-brand-teal/30 rounded-lg hover:bg-brand-teal/20 transition-colors"
          >
            <Sparkles size={12} className="text-brand-teal" />
            Deep Dive
          </button>

          {jobIsActive && !isStale && (
            <button
              onClick={() => cancelJob(job.job_id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-lg hover:bg-rose-100 transition-colors"
            >
              <Ban size={12} />
              Cancel Job
            </button>
          )}
          {(isStale || !jobIsActive) && (
            <button
              onClick={handleArchive}
              disabled={archiving === job.job_id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
            >
              {archiving === job.job_id ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Archive size={12} />
              )}
              Archive Job
            </button>
          )}
        </div>
      </div>

      {/* Stale job warning */}
      {isStale && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-700">
            <p className="font-bold mb-0.5">Job appears stuck</p>
            <p>
              The <span className="font-mono">package</span> phase has been running without progress.
              This usually means the server restarted before the final step could be recorded in Firestore.
              The ad pack may have actually completed — check GCS or archive this job to clear it.
            </p>
          </div>
        </div>
      )}

      {/* Header Card */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-soft">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-navy-900 flex items-center justify-center text-brand-teal">
              <Zap size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-navy-900">Ad Pack Generation</h1>
              <span className="font-mono text-xs text-slate-400">{job.job_id}</span>
            </div>
          </div>
          <StatusBadge status={job.status} />
        </div>

        {/* Progress — only relevant while the pack is still building */}
        {!isCompleted && (
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1"><ProgressBar percent={pct} failed={job.status === 'failed'} /></div>
            <span className="text-lg font-bold text-navy-900">{pct}%</span>
          </div>
        )}

        {/* Metadata grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-xs">
          <MetaItem label="Client" value={job.client_uid || (job.job_id ? job.job_id.split('-')[0] : '-')} />
          <MetaItem label="Current Phase" value={phaseLabel(job.pipeline_phase)} />
          <MetaItem label="Personas" value={String(job.personas_count)} />
          <MetaItem label="Elapsed" value={elapsed(job.elapsed_seconds)} />
          <MetaItem
            label="Est. Cost"
            value={(() => {
              const steps = job.execution_log?.steps ?? [];
              const active = steps.filter((s) => s.status === 'completed' || s.status === 'running');
              const total = estimateTotalCost(active);
              if (total === 0) return '-';
              if (total < 0.01) return `$${total.toFixed(4)}`;
              return `$${total.toFixed(3)}`;
            })()}
          />
        </div>
      </div>

      {/* ── Primary view: embedded share pack (done) or build stages (running) ── */}
      {isCompleted ? (
        <SharePackEmbed
          shareToken={shareToken}
          shareLoading={shareLoading}
          shareError={shareError}
          urlCopied={urlCopied}
          onCopy={handleCopyShareUrl}
          onRetryMint={handleMintShare}
        />
      ) : (
        <>
          {/* Streaming-first viewer pointer — primary destination for
              in-progress packs is /creative/live/:jobId. This page (the
              engineering-facing pipeline view) keeps the inline
              LiveAdPackView too, but nudge users to the dedicated
              streaming surface so they land on the focal experience. */}
          <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-teal-50 px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 rounded-full bg-violet-600 text-white text-[9px] font-bold uppercase tracking-wide">
                New
              </span>
              <p className="text-xs text-slate-700">
                <span className="font-semibold">Live streaming view</span> —
                full-bleed, per-ad hydration as the pipeline runs.
              </p>
            </div>
            <button
              onClick={() => navigate(`/creative/live/${jobId}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition shadow-sm"
            >
              <Sparkles size={12} /> Open streaming view
            </button>
          </div>
          {/*
            Live Asset Streaming view — hydrates per-ad cards as the
            pipeline emits state transitions (decoupled from the canonical
            pack bundle via the adpacks_live Firestore collection). Shows
            for any in-progress job; renders the "Waiting for pipeline"
            stub if the run pre-dates live streaming or didn't opt in.
          */}
          <LiveAdPackView jobId={jobId} className="mb-6" />
        </>
      )}
      {!isCompleted && (
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-6 items-start">
          {/* Left: Phase Timeline */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-brand-purple/10 flex items-center justify-center text-brand-purple border border-brand-purple/20">
                  <Layers size={13} />
                </div>
                <span className="text-sm font-bold text-navy-900 uppercase tracking-wide">Pipeline</span>
              </div>
              <div className="flex items-center gap-2">
                {jobIsActive && followLiveRef.current && (
                  <span className="flex items-center gap-1.5 text-[10px] font-medium text-brand-teal">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-teal opacity-75" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-brand-teal" />
                    </span>
                    Following live
                  </span>
                )}
                {selectedPhase && !followLiveRef.current && (
                  <button
                    onClick={handleClearFilter}
                    className="text-[10px] text-slate-400 hover:text-brand-teal transition-colors"
                  >
                    Show all
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              {hasPhaseData ? (
                <div>
                  {job.phases_completed.map((phase, i) => (
                    <PhaseRow
                      key={phase}
                      phase={phase}
                      status="completed"
                      isLast={i === job.phases_completed.length - 1 && !job.pipeline_phase && job.phases_remaining.length === 0}
                      step={stepByPhase[phase]}
                      selected={selectedPhase === phase}
                      onClick={() => handleSelectPhase(phase)}
                    />
                  ))}
                  {job.pipeline_phase && !job.phases_completed.includes(job.pipeline_phase) && (
                    <PhaseRow
                      phase={job.pipeline_phase}
                      status="running"
                      isLast={job.phases_remaining.length === 0}
                      step={stepByPhase[job.pipeline_phase]}
                      selected={selectedPhase === job.pipeline_phase}
                      onClick={() => handleSelectPhase(job.pipeline_phase!)}
                    />
                  )}
                  {job.phases_remaining.map((phase, i) => (
                    <PhaseRow
                      key={phase}
                      phase={phase}
                      status="pending"
                      isLast={i === job.phases_remaining.length - 1}
                      step={stepByPhase[phase]}
                      selected={selectedPhase === phase}
                      onClick={() => handleSelectPhase(phase)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-400 py-6 text-sm">No pipeline phase data</p>
              )}
            </div>
          </div>

          {/* Right: Execution Log */}
          <ExecutionLogPanel
            executionLog={job.execution_log}
            status={job.status}
            currentPhase={job.pipeline_phase}
            alwaysOpen
            filterPhase={selectedPhase}
            onClearFilter={handleClearFilter}
          />
        </div>
      )}

      {/* Secondary details — collapsed by default once the pack is completed */}
      {isCompleted ? (
        <CompletedPackDetails
          job={job}
          downloading={downloading}
          downloadError={downloadError}
          onDownload={handleDownload}
          stepByPhase={stepByPhase}
          selectedPhase={selectedPhase}
          onSelectPhase={handleSelectPhase}
          onClearFilter={handleClearFilter}
        />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2">
              <User size={14} className="text-slate-400" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Personas</span>
            </div>
            <div className="text-2xl font-bold text-navy-900">{job.personas_count}</div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2">
              <Image size={14} className="text-brand-teal" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Ads Generated</span>
            </div>
            <div className="text-2xl font-bold text-navy-900">{job.ads_generated}</div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={14} className="text-brand-purple" />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Ads Reviewed</span>
            </div>
            <div className="text-2xl font-bold text-navy-900">{job.ads_reviewed}</div>
          </div>
        </div>
      )}

      {/* Error */}
      {job.error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
          <h3 className="text-xs font-bold text-rose-700 uppercase mb-1">Error</h3>
          <p className="text-sm text-rose-600">{job.error}</p>
        </div>
      )}

      {/* Agentic Deep Dive Modal */}
      {showDeepDive && (
        <AgenticDeepDiveModal job={job} onClose={() => setShowDeepDive(false)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({ icon: Icon, label, value, accent }: { icon: React.ElementType; label: string; value: number; accent?: boolean }) {
  return (
    <div className="stat-card">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={accent ? 'text-brand-teal' : 'text-slate-400'} />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-bold text-navy-900">{value}</div>
    </div>
  );
}

function ActiveJobCard({ job, onClick }: { job: AdPackJob; onClick: () => void }) {
  const pct = Math.round(job.percent_complete);
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:border-brand-teal/30 hover:shadow-soft transition-all cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal border border-brand-teal/20">
            <Zap size={16} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-navy-900">Ad Pack Generation</span>
              {job.status === 'running' && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-teal opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-teal" />
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-400 font-mono">{job.job_id}</div>
          </div>
        </div>
        <StatusBadge status={job.status} />
      </div>
      <div className="flex items-center gap-4 mb-3 text-xs text-slate-500">
        <span className="flex items-center gap-1"><User size={10} /> {job.client_uid || '-'}</span>
        <span className="flex items-center gap-1"><Layers size={10} /> {phaseLabel(job.pipeline_phase)}</span>
        <span className="flex items-center gap-1"><Image size={10} /> {job.ads_generated} ads</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1"><ProgressBar percent={pct} failed={job.status === 'failed'} /></div>
        <span className="text-[11px] font-bold text-navy-900 min-w-[3rem] text-right">{pct}%</span>
      </div>
    </div>
  );
}

function PhaseRow({
  phase,
  status,
  isLast,
  step,
  selected,
  onClick,
}: {
  phase: string;
  status: string;
  isLast: boolean;
  step?: StepLogEntry;
  selected: boolean;
  onClick: () => void;
}) {
  const iconBg: Record<string, string> = {
    completed: 'bg-emerald-500 text-white',
    running: 'bg-brand-teal text-white',
    failed: 'bg-rose-500 text-white',
    cancelled: 'bg-slate-400 text-white',
    pending: 'bg-slate-100 text-slate-400',
  };
  const IconMap: Record<string, React.ElementType> = {
    completed: CheckCircle2,
    running: Loader2,
    failed: XCircle,
    cancelled: Ban,
    pending: Clock,
  };
  const Icon = IconMap[status] ?? Clock;
  const hasError = !!step?.error || step?.status === 'failed';
  const duration = step?.duration_seconds != null ? `${step.duration_seconds.toFixed(1)}s` : null;
  const itemsInfo = step && step.items_total > 0 ? `${step.items_completed}/${step.items_total}` : null;

  return (
    <div
      onClick={onClick}
      className={clsx(
        'flex items-start gap-3 pb-4 relative -mx-1 px-1 rounded-lg cursor-pointer transition-colors',
        selected ? 'bg-brand-teal/5' : 'hover:bg-slate-50',
      )}
    >
      {/* Vertical connector */}
      {!isLast && (
        <div className="absolute left-[19px] top-8 bottom-0 w-0.5 bg-slate-200" />
      )}

      {/* Phase icon */}
      <div
        className={clsx(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 transition-all',
          selected && 'ring-2 ring-brand-teal ring-offset-2',
          iconBg[status] ?? iconBg.pending,
        )}
      >
        <Icon size={12} className={clsx(status === 'running' && 'animate-spin')} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-navy-900">{phaseLabel(phase)}</span>
          <StatusBadge status={status} />
          {hasError && <AlertTriangle size={10} className="text-rose-500 flex-shrink-0" />}
        </div>
        {(duration || itemsInfo) && (
          <div className="flex items-center gap-3 mt-0.5">
            {duration && (
              <span className="text-[10px] font-mono text-slate-400">{duration}</span>
            )}
            {itemsInfo && (
              <span className="text-[10px] text-slate-400">{itemsInfo} items</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="text-xs text-slate-400 hover:text-brand-teal transition-colors flex items-center gap-1">
      <ArrowLeft size={12} />
      Back to Jobs
    </button>
  );
}

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-[10px] text-slate-400 uppercase font-bold">{label}</span>
      <div className="font-medium text-navy-900 mt-0.5">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Share pack embed — primary view for completed jobs
// ---------------------------------------------------------------------------

function SharePackEmbed({
  shareToken,
  shareLoading,
  shareError,
  urlCopied,
  onCopy,
  onRetryMint,
}: {
  shareToken: ShareTokenResponse | null;
  shareLoading: boolean;
  shareError: string | null;
  urlCopied: boolean;
  onCopy: () => void;
  onRetryMint: () => void;
}) {
  return (
    <div className="space-y-3">
      {/* Share context card — sits on top of the embedded pack so reviewers
          immediately see the shareable URL + what it's for */}
      <div className="bg-gradient-to-r from-brand-teal/10 via-white to-brand-purple/10 border border-brand-teal/30 rounded-2xl p-5 shadow-soft">
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-brand-teal text-white flex items-center justify-center flex-shrink-0">
              <Share2 size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-bold text-navy-900 leading-snug">Share this ad pack</h2>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Send this link to a client or teammate to review, comment on, and
                take the ad pack with them. No login required.
              </p>
            </div>
          </div>

          {shareToken ? (
            <div className="flex items-center gap-1.5 sm:gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2.5 w-full min-w-0 shadow-sm">
              <Share2 size={14} className="text-slate-500 flex-shrink-0" aria-hidden />
              <span className="flex-1 font-mono text-[11px] sm:text-xs text-slate-700 truncate min-w-0">
                {shareToken.share_url}
              </span>
              <div className="flex items-center gap-0.5 shrink-0 border-l border-slate-200 pl-2 ml-1">
                <button
                  type="button"
                  onClick={onCopy}
                  className="flex items-center justify-center rounded-lg p-2 text-slate-700 hover:text-brand-teal hover:bg-brand-teal/10 active:bg-brand-teal/15 transition-colors"
                  title="Copy URL"
                >
                  {urlCopied ? (
                    <Check size={17} className="text-emerald-600" strokeWidth={2.25} />
                  ) : (
                    <Copy size={17} strokeWidth={2.25} />
                  )}
                </button>
                <a
                  href={shareToken.share_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center rounded-lg p-2 text-slate-700 hover:text-brand-teal hover:bg-brand-teal/10 active:bg-brand-teal/15 transition-colors"
                  title="Open in new tab"
                >
                  <ExternalLink size={17} strokeWidth={2.25} />
                </a>
              </div>
            </div>
          ) : shareLoading ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-slate-500 bg-white border border-slate-200 rounded-lg">
              <Loader2 size={12} className="animate-spin" />
              Creating share link…
            </div>
          ) : (
            <button
              onClick={onRetryMint}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-teal rounded-lg hover:bg-brand-teal-dark transition-colors w-fit"
            >
              <Share2 size={14} />
              Create share link
            </button>
          )}
        </div>

        {shareError && (
          <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-600">
            {shareError}
          </div>
        )}

        {shareToken && (
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-200/70 text-[10px] text-slate-500">
            <span>
              <span className="font-bold text-slate-400 uppercase tracking-wide mr-1">Expires</span>
              {new Date(shareToken.expires_at).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
            <span>
              <span className="font-bold text-slate-400 uppercase tracking-wide mr-1">Edit credits</span>
              {shareToken.edits_remaining} remaining
            </span>
          </div>
        )}
      </div>

      {/* Embedded pack viewer — same SPA a recipient of the share link would see */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-soft overflow-hidden">
        {shareToken ? (
          <iframe
            src={shareToken.share_url}
            title="Ad pack viewer"
            className="w-full block border-0"
            style={{ height: 'calc(100vh - 220px)', minHeight: '720px' }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            {shareLoading ? (
              <>
                <Loader2 size={24} className="animate-spin text-brand-teal mb-3" />
                <p className="text-sm">Preparing the shareable pack view…</p>
              </>
            ) : (
              <>
                <Share2 size={24} className="text-slate-300 mb-3" />
                <p className="text-sm">Create a share link to preview the ad pack here.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Completed pack secondary details — collapsed build log + download info
// ---------------------------------------------------------------------------

function CompletedPackDetails({
  job,
  downloading,
  downloadError,
  onDownload,
  stepByPhase,
  selectedPhase,
  onSelectPhase,
  onClearFilter,
}: {
  job: AdPackStatusResponse;
  downloading: boolean;
  downloadError: string | null;
  onDownload: () => void;
  stepByPhase: Record<string, StepLogEntry>;
  selectedPhase: string | null;
  onSelectPhase: (phase: string) => void;
  onClearFilter: () => void;
}) {
  const [showBuildLog, setShowBuildLog] = useState(false);
  const hasPhaseData = job.phases_completed.length > 0 || job.phases_remaining.length > 0;

  return (
    <div className="space-y-4">
      {/* Output stats + download — compact row */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div>
            <span className="text-[10px] text-slate-400 uppercase font-bold">Pack ID</span>
            <div className="font-mono text-slate-600 mt-0.5 truncate">
              {job.pack_id || job.manifest?.pack_id || '-'}
            </div>
          </div>
          <div>
            <span className="text-[10px] text-slate-400 uppercase font-bold">Ads Generated</span>
            <div className="font-medium text-navy-900 mt-0.5">{job.ads_generated}</div>
          </div>
          <div>
            <span className="text-[10px] text-slate-400 uppercase font-bold">Personas</span>
            <div className="font-medium text-navy-900 mt-0.5">{job.personas_count}</div>
          </div>
          <div>
            <span className="text-[10px] text-slate-400 uppercase font-bold">Mean CES</span>
            <div className="font-bold text-emerald-600 mt-0.5">
              {job.manifest?.summary?.mean_ces
                ? (job.manifest.summary.mean_ces * 100).toFixed(0) + '%'
                : '-'}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-start md:items-end gap-1">
          <button
            onClick={onDownload}
            disabled={downloading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-teal rounded-lg hover:bg-brand-teal-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Download size={14} />
            )}
            {downloading ? 'Downloading…' : 'Download ZIP'}
          </button>
          {downloadError && <p className="text-xs text-rose-500">{downloadError}</p>}
        </div>
      </div>

      {/* Top ranked ads — optional teaser */}
      {job.manifest?.ranked_ads && job.manifest.ranked_ads.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Star size={12} className="text-amber-500" />
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Top Performing Ads
            </span>
          </div>
          <div className="space-y-2">
            {job.manifest.ranked_ads.slice(0, 3).map((ad, i) => (
              <div key={ad.brief_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                <div
                  className={clsx(
                    'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold',
                    i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500',
                  )}
                >
                  {i === 0 ? <Star size={10} /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-navy-900 truncate">{ad.headline}</p>
                  <p className="text-[10px] text-slate-400">{ad.persona}</p>
                </div>
                <span className="text-xs font-bold text-emerald-600">{(ad.ces * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collapsible build log — full pipeline + execution log for debugging */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <button
          onClick={() => setShowBuildLog((v) => !v)}
          className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
        >
          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 border border-slate-200">
            <Layers size={13} />
          </div>
          <span className="text-sm font-bold text-navy-900">Build Log</span>
          <span className="text-[10px] text-slate-400 ml-auto">
            {showBuildLog ? 'Hide pipeline + execution log' : 'Show pipeline + execution log'}
          </span>
        </button>
        {showBuildLog && (
          <div className="p-5 border-t border-slate-100 grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-6 items-start">
            <div className="bg-slate-50 rounded-xl p-5">
              {hasPhaseData ? (
                <div>
                  {job.phases_completed.map((phase, i) => (
                    <PhaseRow
                      key={phase}
                      phase={phase}
                      status="completed"
                      isLast={i === job.phases_completed.length - 1}
                      step={stepByPhase[phase]}
                      selected={selectedPhase === phase}
                      onClick={() => onSelectPhase(phase)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-center text-slate-400 py-6 text-sm">No pipeline phase data</p>
              )}
            </div>
            <ExecutionLogPanel
              executionLog={job.execution_log}
              status={job.status}
              currentPhase={job.pipeline_phase}
              alwaysOpen
              filterPhase={selectedPhase}
              onClearFilter={onClearFilter}
            />
          </div>
        )}
      </div>
    </div>
  );
}

